import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  listInvitations,
  listProspectiveUsers,
  createInvitation,
  deleteInvitation,
  sendInvitationEmail,
  type Invitation,
  type InvitationRole,
  type ProspectiveUser,
} from '../../services/invitations';

const APP_BASE_URL = window.location.origin;

function getInviteUrl(token: string) {
  return `${APP_BASE_URL}/invite/${token}`;
}

function roleLabel(role: InvitationRole) {
  return role === 'coach' ? 'Coach' : 'Player';
}

function roleBadge(role: InvitationRole) {
  return role === 'coach'
    ? 'bg-purple-100 text-purple-700'
    : 'bg-blue-100 text-blue-700';
}

function statusBadge(status: Invitation['status']) {
  return status === 'accepted'
    ? 'bg-green-100 text-green-700'
    : 'bg-yellow-100 text-yellow-700';
}

function formatDate(val: unknown): string {
  if (!val) return '—';
  // Firestore Timestamp or ISO string
  if (typeof val === 'object' && val !== null && 'toDate' in val) {
    return (val as { toDate: () => Date }).toDate().toLocaleDateString();
  }
  if (typeof val === 'string') return new Date(val).toLocaleDateString();
  return '—';
}

export default function AdminInvitations() {
  const { currentUser } = useAuth();

  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [prospective, setProspective] = useState<ProspectiveUser[]>([]);
  const [tab, setTab] = useState<'invitations' | 'waitlist'>('invitations');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // New invitation form
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<InvitationRole>('player');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Copy / email feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [emailSentId, setEmailSentId] = useState<string | null>(null);
  const [emailingId, setEmailingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [inv, pros] = await Promise.all([listInvitations(), listProspectiveUsers()]);
      setInvitations(inv);
      setProspective(pros);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newEmail.trim() || !currentUser) return;
    setCreating(true);
    setCreateError('');
    try {
      await createInvitation(newEmail.trim(), newRole, currentUser.uid);
      setNewEmail('');
      await load();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create invitation');
      console.error(err);
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this invitation?')) return;
    try {
      await deleteInvitation(id);
      setInvitations((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      console.error(err);
    }
  }

  function handleCopy(invitation: Invitation) {
    const url = getInviteUrl(invitation.token);
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(invitation.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }

  async function handleSendEmail(invitation: Invitation) {
    setEmailingId(invitation.id);
    try {
      await sendInvitationEmail(invitation.id);
      setEmailSentId(invitation.id);
      // Refresh list so lastEmailSentAt column updates
      await load();
      setTimeout(() => setEmailSentId(null), 3000);
    } catch (err) {
      console.error('Failed to send invitation email:', err);
      alert('Failed to send email. Please try again.');
    } finally {
      setEmailingId(null);
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Invitations</h1>
      <p className="text-sm text-gray-500 mb-6">Manage invitation-only access. Only invited users can sign up.</p>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 mb-6">
        {(['invitations', 'waitlist'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors capitalize ${
              tab === t
                ? 'border-tennis-green text-tennis-green'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t === 'invitations' ? `Invitations (${invitations.length})` : `Waitlist (${prospective.length})`}
          </button>
        ))}
      </div>

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      {/* ── INVITATIONS TAB ── */}
      {tab === 'invitations' && (
        <>
          {/* Create form */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Send New Invitation</h2>
            <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
                placeholder="Email address"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-tennis-green input-glow"
              />
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as InvitationRole)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-tennis-green bg-white input-glow"
              >
                <option value="player">Player</option>
                <option value="coach">Coach</option>
              </select>
              <button
                type="submit"
                disabled={creating || !newEmail.trim()}
                className="px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-tennis-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors btn-press"
              >
                {creating ? 'Creating…' : 'Create Invitation'}
              </button>
            </form>
            {createError && <p className="text-red-600 text-xs mt-2">{createError}</p>}
          </div>

          {/* Invitations list */}
          {loading ? (
            <div className="text-center py-12 text-gray-500 text-sm">Loading…</div>
          ) : invitations.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No invitations yet</div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Role</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Created</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Last Email Sent</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invitations.map((inv) => (
                    <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-900 font-medium">{inv.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${roleBadge(inv.role)}`}>
                          {roleLabel(inv.role)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(inv.status)}`}>
                          {inv.status === 'accepted' ? 'Accepted' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{formatDate(inv.createdAt)}</td>
                      <td className="px-4 py-3 text-gray-500">{formatDate(inv.lastEmailSentAt) ?? '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {inv.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleCopy(inv)}
                                className="px-2.5 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                              >
                                {copiedId === inv.id ? '✓ Copied' : 'Copy Link'}
                              </button>
                              <button
                                onClick={() => handleSendEmail(inv)}
                                disabled={emailingId === inv.id}
                                className="px-2.5 py-1 text-xs border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed btn-press"
                              >
                                {emailingId === inv.id ? 'Sending…' : emailSentId === inv.id ? '✓ Sent' : 'Send Email'}
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDelete(inv.id)}
                            className="px-2.5 py-1 text-xs border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors btn-press"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── WAITLIST TAB ── */}
      {tab === 'waitlist' && (
        <>
          {loading ? (
            <div className="text-center py-12 text-gray-500 text-sm">Loading…</div>
          ) : prospective.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No waitlist entries yet</div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">How they heard</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {prospective.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-900 font-medium">{p.email}</td>
                      <td className="px-4 py-3 text-gray-600">{p.howDidYouHear || '—'}</td>
                      <td className="px-4 py-3 text-gray-500">{formatDate(p.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
